import { useState, useRef, useEffect } from 'react'
import SharedCommentInput from './SharedCommentInput'
import { useTranslation } from '@/i18n'

interface CommentComposerProps {
  x: number // Canvas position
  y: number // Canvas position
  onSubmit: (content: string, mentionedUserIds?: string[]) => void
  onCancel: () => void
}

const CommentComposer = ({ x, y, onSubmit, onCancel }: CommentComposerProps) => {
  const { t } = useTranslation()
  const [content, setContent] = useState('')
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([])
  const composerRef = useRef<HTMLDivElement>(null)

  // Handle ESC key to cancel
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onCancel])

  // Handle click outside to cancel
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (composerRef.current && !composerRef.current.contains(e.target as Node)) {
        onCancel()
      }
    }

    // Delay to avoid immediate close from the click that opened it
    // Use ref to ensure proper cleanup if component unmounts before timeout fires
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onCancel])

  const handleSubmit = () => {
    if (content.trim()) {
      onSubmit(content.trim(), mentionedUserIds)
    }
  }

  return (
    <div
      ref={composerRef}
      className="border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-dark-card rounded-lg shadow-lg p-3 w-80"
      style={{
        position: 'fixed',
        left: `${x + 50}px`, // Offset from pin position
        top: `${y}px`,
        zIndex: 10001,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        backgroundColor: 'hsl(var(--card))',
      }}
    >
      <SharedCommentInput
        value={content}
        onChange={setContent}
        onSubmit={handleSubmit}
        onMentionedUsersChange={setMentionedUserIds}
        placeholder={t('board:comments.add_placeholder')}
        autoFocus={true}
      />
    </div>
  )
}

export default CommentComposer
